/**
 * PDF Table — reusable table component for Invoice and Statement PDFs.
 *
 * Lifted verbatim from ledger-flow-ifrs-beacon
 * `domains/invoicing/components/react-pdf/core/PDFTable.tsx`. Drop-in compatible.
 */

import * as React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { documentStyles } from './styles';

export interface TableColumn {
  key: string;
  header: string;
  /** CSS-style width, e.g. "12%" or "120px". */
  width: string;
  align?: 'left' | 'center' | 'right';
}

export interface PDFTableProps {
  columns: TableColumn[];
  data: Record<string, unknown>[];
  showHeader?: boolean;
  alternateRowColors?: boolean;
}

export const PDFTable: React.FC<PDFTableProps> = ({
  columns,
  data,
  showHeader = true,
  alternateRowColors = true,
}) => {
  const getAlignStyle = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center':
        return documentStyles.tableCellCenter;
      case 'right':
        return documentStyles.tableCellRight;
      default:
        return documentStyles.tableCellLeft;
    }
  };

  return (
    <View style={documentStyles.table}>
      {showHeader && (
        <View style={documentStyles.tableHeader}>
          {columns.map((col) => (
            <View key={col.key} style={{ width: col.width }}>
              <Text style={[documentStyles.tableCellHeader, getAlignStyle(col.align)]}>
                {col.header}
              </Text>
            </View>
          ))}
        </View>
      )}

      {data.map((row, rowIndex) => (
        <View
          key={rowIndex}
          style={[
            documentStyles.tableRow,
            alternateRowColors && rowIndex % 2 === 1 ? documentStyles.tableRowAlt : {},
          ]}
        >
          {columns.map((col) => {
            const cellValue = String(row[col.key] ?? '');
            const hasNewlines = cellValue.includes('\n');
            return (
              <View key={col.key} style={{ width: col.width }}>
                {hasNewlines ? (
                  <View style={[documentStyles.tableCell, getAlignStyle(col.align)]}>
                    {cellValue.split('\n').map((line, i) => (
                      <Text key={i}>{line}</Text>
                    ))}
                  </View>
                ) : (
                  <Text style={[documentStyles.tableCell, getAlignStyle(col.align)]}>
                    {cellValue}
                  </Text>
                )}
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
};
